import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar, Button, Stack, Typography } from "@mui/material";
import { Pencil } from "lucide-react";
import { useSelector } from "react-redux";

import { UIRating, UITable } from "../../../ui";
import UICheckbox from "../../../ui/UICheckbox";
import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import { getUserInitials } from "../../../ui/UIUserInfo";
import { clx } from "../../../../utils/Helpers";
import { joiningRequestStatuses } from "./constants";

/** Normalize mock/API values like "150 Hr" to localized "{n} {unit}" */
const formatWorkingHoursCell = (value, unitLabel) => {
  const s = String(value ?? "").trim();
  if (!s) return unitLabel ? `0 ${unitLabel}`.trim() : "0";
  const m = s.match(/^(\d+(?:[.,]\d+)?)\s*(?:hr|hrs|hour|hours|h)?\.?\s*$/i);
  if (m) return `${m[1]} ${unitLabel}`.trim();
  return s
    .replace(/\bhours\b/gi, unitLabel)
    .replace(/\bhour\b/gi, unitLabel)
    .replace(/\bhrs\b/gi, unitLabel)
    .replace(/\bhr\b/gi, unitLabel);
};

const renderTagCell = (row, labels, canEditTags, canManageRowTag, onTagClick) => {
  // Case 3: has tag but can't be edited — muted pill, no icon
  const readonlyPill = row.userTag ? (
    <span className="event-applicants__tag-pill event-applicants__tag-pill--readonly">
      {row.tagName}
    </span>
  ) : (
    <Typography variant="caption" className="event-applicants__label">
      —
    </Typography>
  );

  if (!canEditTags) return readonlyPill;

  // Case 1: no tag — interactive pill, no icon
  if (!row.userTag) {
    return (
      <Button
        size="small"
        variant="outlined"
        dir="auto"
        className="event-applicants__tag-pill event-applicants__tag-pill--interactive"
        onClick={() => onTagClick(row)}
      >
        {labels.addTag}
      </Button>
    );
  }

  const canManage = canManageRowTag ? canManageRowTag(row) : true;
  if (!canManage) return readonlyPill;

  // Case 2: has tag, can edit — interactive pill with Pencil icon on the right
  return (
    <Button
      size="small"
      variant="outlined"
      dir="auto"
      className="event-applicants__tag-pill event-applicants__tag-pill--interactive"
      endIcon={<Pencil size={14} strokeWidth={1.5} />}
      onClick={() => onTagClick(row)}
    >
      {row.tagName}
    </Button>
  );
};

const ApplicantsTable = ({
  rows,
  activeStatus,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onTagClick,
  onRowClick,
  labels,
  showSelection = true,
  canEditTags = true,
  canManageRowTag,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const allSelected =
    rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  const headerIndeterminate =
    rows.length > 0 && selectedIds.length > 0 && !allSelected;

  const columns = useMemo(() => {
    const isJoinedStatus = activeStatus === joiningRequestStatuses.joined;
    const isRemovedStatus = activeStatus === joiningRequestStatuses.removed;
    const roleLabel = isJoinedStatus ? labels.role : labels.requestedRole;
    const textColumn = (column) => ({
      align: "left",
      headerAlign: "left",
      // Content-sized columns (no auto flex) keep an even gap between them;
      // the last column takes the remaining width.
      autoFlex: false,
      ...column,
    });
    const baseColumns = [
      ...(showSelection
        ? [
            {
              field: "select",
              headerName: "",
              sortable: false,
              width: 64,
              minWidth: 64,
              align: "center",
              headerAlign: "center",
              cellClassName: "event-applicants__select-cell",
              headerClassName: "event-applicants__select-header",
              renderHeader: () => (
                <span
                  className="event-applicants__checkbox-control"
                  onClick={(event) => event.stopPropagation()}
                >
                  <UICheckbox
                    className="event-applicants__checkbox"
                    checked={allSelected}
                    indeterminate={headerIndeterminate}
                    onChange={(checked) => onSelectAll(checked)}
                    label=""
                  />
                </span>
              ),
              renderCell: ({ row }) => (
                <span
                  className="event-applicants__checkbox-control"
                  onClick={(event) => event.stopPropagation()}
                >
                  <UICheckbox
                    className="event-applicants__checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={(checked) => onSelectRow(row.id, checked)}
                    label=""
                  />
                </span>
              ),
            },
          ]
        : []),
      textColumn({
        field: "userId",
        sortable: false,
        headerName: labels.id,
        minWidth: 120,
        cellClassName: "event-applicants__id-cell",
        headerClassName: "event-applicants__id-header",
        renderCell: ({ row, value }) => {
          const id = String(row.userId ?? value ?? "").replace(/^#/, "");
          return (
            <span className="event-applicants__id-value MuiDataGrid-cellContent">
              {id ? `#${id}` : "—"}
            </span>
          );
        },
      }),
      textColumn({
        field: "name",
        headerName: labels.staffApplicant,
        sortable: false,
        minWidth: 320,
        renderCell: ({ row }) => (
          <div
            className={clx(
              "event-applicants__name-cell",
              onRowClick && "event-applicants__name-cell--clickable",
            )}
          >
            <Avatar
              src={row.image || undefined}
              alt={row.name}
              className="event-applicants__avatar"
            >
              {getUserInitials(row.name)}
            </Avatar>
            <div className="event-applicants__name-text">
              <TruncatedWithTooltip title={row.name}>
                <span className="event-applicants__name MuiDataGrid-cellContent">
                  {row.name}
                </span>
              </TruncatedWithTooltip>
            </div>
          </div>
        ),
      }),
      textColumn({
        field: "nationality",
        headerName: labels.nationality,
        sortable: false,
        minWidth: 220,
        renderCell: ({ value }) => (
          <TruncatedWithTooltip title={value}>
            <span className="MuiDataGrid-cellContent">{value}</span>
          </TruncatedWithTooltip>
        ),
      }),
      isJoinedStatus &&
        textColumn({
          field: "zone",
          headerName: labels.zone,
          sortable: false,
          minWidth: 150,
          renderCell: ({ value }) => (
            <span className="MuiDataGrid-cellContent">{value || "-"}</span>
          ),
        }),
      textColumn({
        field: "requestedRole",
        sortable: false,
        headerName: roleLabel,
        minWidth: 190,
        renderCell: ({ value }) => (
          <span className="MuiDataGrid-cellContent">{value}</span>
        ),
      }),
      isRemovedStatus &&
        textColumn({
          field: "removedDate",
          sortable: false,
          headerName: labels.removedDate,
          minWidth: 140,
          renderCell: ({ value }) => (
            <span className="MuiDataGrid-cellContent">{value || "—"}</span>
          ),
        }),
      !isJoinedStatus &&
        !isRemovedStatus &&
        textColumn({
          field: "requestDate",
          sortable: false,
          headerName: labels.requestDate,
          minWidth: 170,
          renderCell: ({ value }) => (
            <span className="MuiDataGrid-cellContent">{value}</span>
          ),
        }),
      !isRemovedStatus &&
        textColumn({
          field: "shift",
          sortable: false,
          headerName: labels.shift,
          minWidth: 130,
          renderCell: ({ value }) => (
            <span className="MuiDataGrid-cellContent">{value}</span>
          ),
        }),
    ].filter(Boolean);

    if (isRemovedStatus) {
      baseColumns.push(
        textColumn({
          field: "rating",
          headerName: labels.rating,
          sortable: false,
          minWidth: 100,
          renderCell: ({ value }) => (
            <UIRating value={value ?? 0} variant="compact" />
          ),
        }),
        textColumn({
          field: "removalReason",
          headerName: labels.removeReason,
          sortable: false,
          flex: 1,
          minWidth: 180,
          renderCell: ({ value }) => (
            <TruncatedWithTooltip title={value}>
              <span className="MuiDataGrid-cellContent">{value || "—"}</span>
            </TruncatedWithTooltip>
          ),
        }),
      );

      return baseColumns;
    }

    if (isJoinedStatus) {
      baseColumns.push(
        textColumn({
          field: "checkIn",
          sortable: false,
          headerName: labels.checkIn,
          minWidth: 170,
          flex: 0,
          renderCell: ({ row }) => (
            <Stack
              direction="row"
              alignItems="center"
              className="event-applicants__check-in"
            >
              <span
                className={
                  row.is_user_checkedin
                    ? "event-applicants__check-in-dot event-applicants__check-in-dot--checked"
                    : "event-applicants__check-in-dot"
                }
              />
              <span className="MuiDataGrid-cellContent">
                {row.is_user_checkedin ? labels.checkedIn : labels.nonCheckedIn}
              </span>
            </Stack>
          ),
        }),
      );
    }

    if (isJoinedStatus) {
      baseColumns.push(
        textColumn({
          field: "rating",
          headerName: labels.rating,
          sortable: false,
          minWidth: 100,
          renderCell: ({ value }) => (
            <UIRating value={value ?? 0} variant="compact" />
          ),
        }),
        textColumn({
          field: "userTag",
          headerName: labels.tags,
          sortable: false,
          flex: 1.5,
          minWidth: 220,
          align: "center",
          headerAlign: "center",
          renderCell: ({ row }) =>
            renderTagCell(row, labels, canEditTags, canManageRowTag, onTagClick),
        }),
      );

      return baseColumns;
    }

    baseColumns.push(
      textColumn({
        field: "workingHours",
        sortable: false,
        headerName: labels.workingHours,
        minWidth: 140,
        renderCell: ({ value }) => (
          <span className="MuiDataGrid-cellContent">
            {formatWorkingHoursCell(value, labels.workingHoursUnit)}
          </span>
        ),
      }),
      textColumn({
        field: "rating",
        headerName: labels.rating,
        flex: 1,
        minWidth: 100,
        renderCell: ({ value }) => (
          <UIRating value={value ?? 0} variant="compact" />
        ),
      }),
    );

    return baseColumns;
  }, [
    activeStatus,
    allSelected,
    canEditTags,
    canManageRowTag,
    headerIndeterminate,
    labels,
    onTagClick,
    onRowClick,
    onSelectAll,
    onSelectRow,
    selectedIds,
    showSelection,
  ]);

  const shouldIgnoreApplicantRowNavigate = (event) =>
    event?.target?.closest?.(
      ".event-applicants__checkbox-control, .event-applicants__tag-pill, button, a, input, .MuiCheckbox-root",
    );

  const handleApplicantNavigate = onRowClick
    ? (params, event) => {
        if (shouldIgnoreApplicantRowNavigate(event)) return;
        onRowClick(params.row);
      }
    : undefined;

  return (
    <div
      className={clx(
        "event-applicants__table",
        onRowClick && "event-applicants__table--clickable-rows",
      )}
    >
      <UITable
        key={lang}
        rows={rows}
        columns={columns}
        columnBuffer={columns.length}
        contentSized
        fillAvailableWidth
        striped
        hideFooter
        disableColumnMenu
        disableSelectionOnClick
        getRowId={(row) => row.id}
        rowHeight={72}
        onCellClick={handleApplicantNavigate}
      />
    </div>
  );
};

ApplicantsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeStatus: PropTypes.string.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectRow: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onTagClick: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  showSelection: PropTypes.bool,
  canEditTags: PropTypes.bool,
  canManageRowTag: PropTypes.func,
  labels: PropTypes.shape({
    id: PropTypes.string.isRequired,
    staffApplicant: PropTypes.string.isRequired,
    nationality: PropTypes.string.isRequired,
    zone: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    requestedRole: PropTypes.string.isRequired,
    requestDate: PropTypes.string.isRequired,
    removedDate: PropTypes.string,
    removeReason: PropTypes.string,
    shift: PropTypes.string.isRequired,
    checkIn: PropTypes.string.isRequired,
    checkedIn: PropTypes.string.isRequired,
    nonCheckedIn: PropTypes.string.isRequired,
    workingHours: PropTypes.string.isRequired,
    rating: PropTypes.string.isRequired,
    tags: PropTypes.string.isRequired,
    addTag: PropTypes.string.isRequired,
    workingHoursUnit: PropTypes.string.isRequired,
  }).isRequired,
};

export default ApplicantsTable;
